/* NotificationsProvider — WebSocket live push + REST fallback.
   Beheert unread count + notificatielijst + toast bij nieuwe ophaal-events. */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { user, status } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);

  const loadFromApi = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (_) { /* stille fallback */ }
  }, []);

  const handleMessage = useCallback((msg) => {
    if (msg.event === "connected") {
      setUnread(msg.unread || 0);
      return;
    }
    if (msg.event === "notification" && Array.isArray(msg.notifications)) {
      setItems(prev => [...msg.notifications, ...prev].slice(0, 50));
      setUnread(prev => prev + msg.notifications.length);
      msg.notifications.forEach(n => {
        if (n.type === "ophaal") {
          toast.info(`${n.title}`, { description: n.body, duration: 8000 });
        } else {
          toast.info(n.title, { description: n.body });
        }
      });
      return;
    }
    // Real-time chat/activity broadcasts — feed door aan chat-UIs
    if (msg.event === "new_message" && msg.message) {
      window.dispatchEvent(new CustomEvent("chat:new_message", { detail: msg }));
    }
  }, []);

  const connect = useCallback(() => {
    if (status !== "authed" || !user) return;
    try {
      const url = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws/notifications";
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        // Ping every 30s to keep connection alive
        clearInterval(pingTimer.current);
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 30000);
      };
      ws.onmessage = (evt) => {
        if (evt.data === "pong") return;
        try {
          const msg = JSON.parse(evt.data);
          handleMessage(msg);
        } catch (_) {}
      };
      ws.onerror = () => { /* stil */ };
      ws.onclose = () => {
        clearInterval(pingTimer.current);
        // Reconnect met exponentiële back-off (max 30s)
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(connect, 5000);
      };
    } catch (_) {}
  }, [status, user, handleMessage]);

  useEffect(() => {
    if (status !== "authed" || !user) {
      // Cleanup bij logout
      try { wsRef.current?.close(); } catch (_) {}
      clearInterval(pingTimer.current);
      clearTimeout(reconnectTimer.current);
      setItems([]); setUnread(0);
      return;
    }
    loadFromApi();
    connect();
    return () => {
      try { wsRef.current?.close(); } catch (_) {}
      clearInterval(pingTimer.current);
      clearTimeout(reconnectTimer.current);
    };
  }, [status, user?.id, connect, loadFromApi]);

  const markRead = useCallback(async (ids) => {
    try {
      await api.post("/notifications/mark-read", ids ? { ids } : {});
      if (ids) {
        setItems(prev => prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n));
        setUnread(prev => Math.max(0, prev - ids.length));
      } else {
        setItems(prev => prev.map(n => ({ ...n, read: true })));
        setUnread(0);
      }
    } catch (_) {}
  }, []);

  return (
    <NotificationsContext.Provider value={{ items, unread, markRead, reload: loadFromApi }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationsContext) || { items: [], unread: 0, markRead: () => {}, reload: () => {} };
